import React, { useState, useEffect, useMemo } from "react";
import { withRouter, useParams, useLocation } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";

import ACTION from "../store/action";
import {
  selectIsFetching,
  selectFilterList,
  selectFilterListStatuses,
  selectIsFilterListFiltersModified,
  selectTableColumnsModified,
  selectTableLayoutModified,
  selectLayoutResetNonce,
  selectAllPatientsData,
  selectVisitScheduleData,
  selectHighPrioritiesData,
  selectAllApplicationsData,
  selectPendingRenewalData,
  selectAllClaimsData,
  selectExportInProgress
} from "../store/selector";
import { AllPatientsTable } from "../component/TableSection/AllPatientsTable";
import { VisitScheduleTable } from "../component/TableSection/VisitScheduleTable";
import { HighPrioritiesTable } from "../component/TableSection/HighPrioritiesTable";
import { BulkActionsContainer } from "../component/BulkActions";
import { PendingRenewalTable } from "../component/TableSection/PendingRenewalTable";
import { AllApplicationsTable } from "../component/TableSection/AllApplicationsTable";
import { PatientEnrollSentTable } from "../component/TableSection/PatientEnrollSent/PatientEnrollSentTable";
import { FilterListControls } from "../component/FilterList/FilterListControls";
import { ApiNames } from "../api/api";
import TableFilters from "../component/TableFilters";
import { selectedViews } from "../constant/table";
import { getColumnLayoutStorageKey } from "../utils/columnLayout";
import { FilterListForm } from "../component/FormPopups/FilterListForm";
import { AllClaimsTable } from "../component/TableSection/AllClaimsTable";
import ACTIONS from "../store/action";
import { WHITE, GRAY_100 } from "../constant/colors";
import { Skeleton } from "antd";

const DashboardScreen = styled.div`
  margin: 0px 15px 0px 15px;
  position: relative;
  background: ${WHITE};
  border-radius: 10px;
  padding: 10px 15px 0px 15px;
`;

const FilterSectionWrapper = styled.div`
  flex: 0 0 288px;
  max-width: ${(props) => {
    if (props.filtersVisible) {
      return "288px";
    }
    return "0px";
  }};
  transition: max-width 0.3s ease;
`;

const TableSectionWrapper = styled.div`
  flex: 1 1 auto;
  overflow: hidden;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${GRAY_100};
  margin: 5px 0;
`;

const DEFAULT_PAGE = 1;
const DEFAULT_SORTER = {};

const FilterListViewComponent = ({
  isFetching,
  searchSavedListByIdAct,
  filterList,
  filterListStatuses,
  markSavedFilterAsChanged,
  filterListFiltersModified,
  tableColumnsModified,
  tableLayoutModified,
  layoutResetNonce,
  updateSavedFilterViewParameters,
  clearSorterAct
}) => {
  const dispatch = useDispatch();
  const [filtersVisible, setFiltersVisible] = useState(false);
  const filterListId = parseInt(useParams().filterListId);
  const location = useLocation();
  const isReturningFromPatientPage = location.state?.isReturningFromPatientPage;
  const updateSavedFilterViewParametersAct = updateSavedFilterViewParameters(filterListId);
  const clearTablesDataAct = (selectedView) => dispatch(ACTION.clearTablesData(selectedView));

  // List-scoped key so each saved list restores its own column layout without
  // touching the shared main-view layout.
  const columnLayoutKey = getColumnLayoutStorageKey(filterList?.selectedView, filterList?.id);

  // Get data for bulk selection
  const allPatientsData = useSelector(selectAllPatientsData);
  const visitScheduleData = useSelector(selectVisitScheduleData);
  const highPrioritiesData = useSelector(selectHighPrioritiesData);
  const allApplicationsData = useSelector(selectAllApplicationsData);
  const pendingRenewalData = useSelector(selectPendingRenewalData);
  const allClaimsData = useSelector(selectAllClaimsData);
  // Drives the bulk-actions bar's "Exporting data…" state; export is triggered from the header menu
  // (a sibling component), so the flag is shared through the store.
  const isExporting = useSelector(selectExportInProgress);

  // Extract visible patient IDs based on the selected view
  const visiblePatientIds = useMemo(() => {
    if (filterList?.selectedView === selectedViews.allPatients) {
      return (allPatientsData?.patients || []).map((patient) => patient.patientId).filter(Boolean);
    }
    if (filterList?.selectedView === selectedViews.visitSchedule) {
      return (visitScheduleData?.patients || []).map((patient) => patient.patientId).filter(Boolean);
    }
    if (filterList?.selectedView === selectedViews.highPriority) {
      return (highPrioritiesData?.patients || []).map((patient) => patient.patientId).filter(Boolean);
    }
    return [];
  }, [filterList?.selectedView, allPatientsData?.patients, visitScheduleData?.patients, highPrioritiesData?.patients]);

  // Extract visible enrollment IDs based on the selected view
  const visibleEnrollmentIds = useMemo(() => {
    if (filterList?.selectedView === selectedViews.allApplications) {
      return (allApplicationsData?.patients || []).map((enrollment) => enrollment.applicationId).filter(Boolean);
    }
    if (filterList?.selectedView === selectedViews.pendingRenewal) {
      return (pendingRenewalData?.patients || []).map((enrollment) => enrollment.applicationId).filter(Boolean);
    }
    return [];
  }, [filterList?.selectedView, allApplicationsData?.patients, pendingRenewalData?.patients]);

  // Extract visible claim IDs based on the selected view
  const visibleClaimIds = useMemo(() => {
    if (filterList?.selectedView === selectedViews.allClaims) {
      return (allClaimsData?.patients || []).map((claim) => claim.claimId).filter(Boolean);
    }
    return [];
  }, [filterList?.selectedView, allClaimsData?.patients]);

  // Get total records for the current view
  const totalRecords = useMemo(() => {
    if (filterList?.selectedView === selectedViews.allPatients) {
      return allPatientsData?.totalPatients || 0;
    }
    if (filterList?.selectedView === selectedViews.visitSchedule) {
      return visitScheduleData?.totalPatients || 0;
    }
    if (filterList?.selectedView === selectedViews.highPriority) {
      return highPrioritiesData?.totalPatients || 0;
    }
    if (filterList?.selectedView === selectedViews.allApplications) {
      return allApplicationsData?.totalPatients || 0;
    }
    if (filterList?.selectedView === selectedViews.pendingRenewal) {
      return pendingRenewalData?.totalPatients || 0;
    }
    if (filterList?.selectedView === selectedViews.allClaims) {
      return allClaimsData?.totalPatients || 0;
    }
    return 0;
  }, [
    filterList?.selectedView,
    allPatientsData?.totalPatients,
    visitScheduleData?.totalPatients,
    highPrioritiesData?.totalPatients,
    allApplicationsData?.totalPatients,
    pendingRenewalData?.totalPatients,
    allClaimsData?.totalPatients
  ]);

  useEffect(() => {
    if (filterListId && filterListStatuses.id !== filterListId) {
      //Note: Reset the filter parameters when changing the view to another filter page.
      updateSavedFilterViewParametersAct(DEFAULT_PAGE, DEFAULT_SORTER);
    }

    // Note: we need to fetch data always to make data consistent for the user.
    searchSavedListByIdAct(filterListId);

    if (filterListId) {
      // Note: Reset the filter modified status to mark it as not modified.
      markSavedFilterAsChanged(filterListId, false);
    }
  }, [filterListId]);
  useEffect(() => {
    // Clear table sorting when accessing saved list to ensure clean state on entry,
    // but skip when returning from patient detail to preserve page/sort position
    if (filterList?.selectedView && !isReturningFromPatientPage) {
      clearSorterAct(filterList.selectedView);
    }
  }, [filterListId, filterList?.selectedView]);

  useEffect(() => {
    // Clear all table data when switching to a new saved list
    if (filterList?.selectedView && (!filterListId || (filterListId && filterListStatuses.id !== filterListId))) {
      clearTablesDataAct(filterList?.selectedView);
    }

    if (filterList?.id && (tableColumnsModified || filterListFiltersModified || tableLayoutModified)) {
      markSavedFilterAsChanged(filterList?.id, true);
    }
  }, [tableColumnsModified, filterListFiltersModified, tableLayoutModified]);

  return (
    <DashboardScreen direction={"vertical"}>
      {!isFetching ? (
        <>
          <FilterListControls searchSavedListByIdAct={searchSavedListByIdAct} filterListId={filterListId} />
          <Divider />
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              transition: "all 0.3s ease"
            }}
          >
            <FilterSectionWrapper filtersVisible={filtersVisible}>
              <TableFilters
                filtersVisible={filtersVisible}
                setFiltersVisible={setFiltersVisible}
                showSaveNewListBtn={false}
                isFilterListView={true}
              />
            </FilterSectionWrapper>
            <TableSectionWrapper>
              {filterList?.selectedView === selectedViews.allPatients && (
                <BulkActionsContainer
                  viewKey="all-patients"
                  isExporting={isExporting}
                  entityType="patient"
                  visibleRecordIds={visiblePatientIds}
                  totalRecords={totalRecords}
                  rowKey="patientId"
                >
                  {({ rowSelection }) => (
                    <AllPatientsTable
                      key={`${filterList?.id}-${layoutResetNonce}`}
                      isSearch={false}
                      updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                      isFilterListView={true}
                      columnLayoutKey={columnLayoutKey}
                      rowSelection={rowSelection}
                    />
                  )}
                </BulkActionsContainer>
              )}
              {filterList?.selectedView === selectedViews.visitSchedule && (
                <BulkActionsContainer
                  viewKey="visit-schedule"
                  isExporting={isExporting}
                  entityType="patient"
                  visibleRecordIds={visiblePatientIds}
                  totalRecords={totalRecords}
                  rowKey="patientId"
                >
                  {({ rowSelection }) => (
                    <VisitScheduleTable
                      key={`${filterList?.id}-${layoutResetNonce}`}
                      updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                      isFilterListView={true}
                      columnLayoutKey={columnLayoutKey}
                      rowSelection={rowSelection}
                    />
                  )}
                </BulkActionsContainer>
              )}
              {filterList?.selectedView === selectedViews.pendingRenewal && (
                <BulkActionsContainer
                  viewKey="pending-renewal"
                  isExporting={isExporting}
                  entityType="enrollment"
                  visibleRecordIds={visibleEnrollmentIds}
                  totalRecords={totalRecords}
                  rowKey="journeyId"
                >
                  {({ rowSelection }) => (
                    <PendingRenewalTable
                      key={`${filterList?.id}-${layoutResetNonce}`}
                      updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                      isFilterListView={true}
                      columnLayoutKey={columnLayoutKey}
                      rowSelection={rowSelection}
                    />
                  )}
                </BulkActionsContainer>
              )}
              {filterList?.selectedView === selectedViews.highPriority && (
                <BulkActionsContainer
                  viewKey="high-priority"
                  isExporting={isExporting}
                  entityType="patient"
                  visibleRecordIds={visiblePatientIds}
                  totalRecords={totalRecords}
                  rowKey="patientId"
                >
                  {({ rowSelection }) => (
                    <HighPrioritiesTable
                      key={`${filterList?.id}-${layoutResetNonce}`}
                      updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                      isFilterListView={true}
                      columnLayoutKey={columnLayoutKey}
                      rowSelection={rowSelection}
                    />
                  )}
                </BulkActionsContainer>
              )}
              {filterList?.selectedView === selectedViews.allApplications && (
                <BulkActionsContainer
                  viewKey="all-applications"
                  isExporting={isExporting}
                  entityType="enrollment"
                  visibleRecordIds={visibleEnrollmentIds}
                  totalRecords={totalRecords}
                  rowKey="journeyId"
                >
                  {({ rowSelection }) => (
                    <AllApplicationsTable
                      key={`${filterList?.id}-${layoutResetNonce}`}
                      updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                      isFilterListView={true}
                      columnLayoutKey={columnLayoutKey}
                      rowSelection={rowSelection}
                    />
                  )}
                </BulkActionsContainer>
              )}
              {filterList?.selectedView === selectedViews.allClaims && (
                <BulkActionsContainer
                  viewKey="all-claims"
                  isExporting={isExporting}
                  entityType="claim"
                  visibleRecordIds={visibleClaimIds}
                  totalRecords={totalRecords}
                  rowKey="claimId"
                >
                  {({ rowSelection }) => (
                    <AllClaimsTable
                      key={`${filterList?.id}-${layoutResetNonce}`}
                      updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                      isFilterListView={true}
                      columnLayoutKey={columnLayoutKey}
                      rowSelection={rowSelection}
                    />
                  )}
                </BulkActionsContainer>
              )}
              {filterList?.selectedView === selectedViews.patientActions && (
                <PatientEnrollSentTable
                  isFilterListView={true}
                  updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                />
              )}
            </TableSectionWrapper>
            <FilterListForm />
          </div>
        </>
      ) : (
        <>
          <Skeleton.Button style={{ width: 100, height: 40 }} active />
          <Skeleton style={{ paddingTop: 16 }} active paragraph={{ rows: 6 }} />
          <Skeleton style={{ paddingTop: 32 }} active paragraph={{ rows: 6 }} />
        </>
      )}
    </DashboardScreen>
  );
};

const mapDispatchToProps = (dispatch) => ({
  searchSavedListByIdAct: (filterListId) => dispatch(ACTION.searchSavedListByIdAction(filterListId)),
  markSavedFilterAsChanged: (filterListID, isModified) =>
    dispatch(ACTIONS.markSavedFilterAsChanged(filterListID, isModified)),
  updateSavedFilterViewParameters: (filterListID) => (page, sorter) =>
    dispatch(ACTION.updateSavedFilterViewParameters(filterListID, page, sorter)),
  clearSorterAct: (view) => dispatch(ACTION.clearSorterAction(view))
});
const mapStateToProps = createStructuredSelector({
  isFetching: selectIsFetching([ApiNames.FindSavedListById]),
  filterList: selectFilterList,
  filterListStatuses: selectFilterListStatuses,
  filterListFiltersModified: selectIsFilterListFiltersModified,
  tableColumnsModified: selectTableColumnsModified,
  tableLayoutModified: selectTableLayoutModified,
  layoutResetNonce: selectLayoutResetNonce
});

const FilterListView = compose(
  withTranslation(),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(FilterListViewComponent);
export { FilterListView };
